import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { FaTrash, FaMinus, FaPlus, FaShoppingCart, FaArrowLeft, FaCalendar, FaClock, FaUsers, FaInfoCircle } from 'react-icons/fa';
import CountdownTimer from '../components/CountdownTimer';
import toast from 'react-hot-toast';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { getNextDeliveryDate, isOrderingCurrentlyOpen, settings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [showOrderInfo, setShowOrderInfo] = useState(false);

  // Society pre-order specific data
  const deliveryDate = getNextDeliveryDate();

  if (!user) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🔐</div>
        <h2>Please login to view your pre-orders</h2>
        <p className="mb-6">Sign in to access your society pre-order cart and manage your orders.</p>
        <button onClick={() => navigate('/login')} className="btn btn-primary btn-lg">
          Login
        </button>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🛒</div>
        <h2>Your pre-order cart is empty</h2>
        <p className="mb-6">Start building your society's pre-order for the upcoming delivery cycle!</p>
        <div className="empty-cart-actions">
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/products')}>Browse Pre-Orders</button>
          <button className="btn btn-outline-primary btn-lg" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  if (!settings) {
    return <div>Loading settings...</div>;
  }

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(itemId, newQuantity);
    toast.success('Pre-order updated!');
  };

  const handleRemoveItem = (item) => {
    removeFromCart(item._id);
    toast.success(`${item.name} removed from pre-order`);
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your pre-order cart?')) {
      clearCart();
      toast.success('Pre-order cart cleared');
    }
  };

  const handleCheckout = () => {
    if (!isOrderingCurrentlyOpen()) {
      toast.error('Ordering is currently closed. Please wait for the next cycle.');
      return;
    }
    
    setLoading(true);
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
      navigate('/checkout');
    }, 1000);
  };

  const subtotal = getCartTotal();
  
  // Calculate delivery fee based on settings
  let deliveryFee = 0;
  
  if (settings?.enableDeliveryFee) {
    if (settings?.freeDeliveryEnabled && subtotal >= settings.freeDeliveryThreshold) {
      deliveryFee = 0; // Free delivery
    } else {
      deliveryFee = settings?.deliveryFee || 0;
    }
  }
  
  const total = subtotal + deliveryFee;

  return (
    <div className="container">
      {/* Pre-Order Information Banner */}
      <div className="card mb-4" style={{ 
        background: 'linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%)',
        color: 'white',
        border: 'none',
        boxShadow: 'var(--shadow-lg)'
      }}>
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
            <div className="mb-3 mb-md-0">
              <h3 className="mb-2">
                <FaUsers className="me-2" />
                Society Pre-Order
              </h3>
              <p className="mb-0">Manage your pre-orders for batch delivery</p>
            </div>
            <div className="d-flex align-items-center">
              <div className="d-none d-md-block me-3" style={{ width: '1px', height: '40px', background: 'rgba(255,255,255,0.3)' }}></div>
              <button 
                className="btn btn-outline-light btn-sm"
                onClick={() => setShowOrderInfo(!showOrderInfo)}
              >
                <FaInfoCircle className="me-1" />
                Order Info
              </button>
            </div>
          </div>
          
          {showOrderInfo && (
            <div className="mt-4 p-3 bg-white bg-opacity-20 rounded">
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <div className="text-center">
                    <CountdownTimer showWarning={false} lightTheme={true} />
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="d-none d-md-block me-3" style={{ width: '1px', height: '80px', background: 'rgba(255,255,255,0.3)' }}></div>
                    <div className="text-center text-md-start">
                      <div className="d-flex align-items-center justify-content-center justify-content-md-start mb-2">
                        <FaCalendar className="me-2 text-white" />
                        <strong className="text-white">Delivery Date:</strong>
                      </div>
                      <p className="mb-0 text-white fw-bold">{deliveryDate.toLocaleDateString('en-GB')} ({deliveryDate.toLocaleDateString('en-US', { weekday: 'long' })})</p>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="d-none d-md-block me-3" style={{ width: '1px', height: '80px', background: 'rgba(255,255,255,0.3)' }}></div>
                    <div className="text-center text-md-start">
                      <div className="d-flex align-items-center justify-content-center justify-content-md-start mb-2">
                        <FaUsers className="me-2 text-white" />
                        <strong className="text-white">Batch Processing:</strong>
                      </div>
                      <p className="mb-0 text-white">All society orders delivered together</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 gap-3">
        <h1 className="mb-0">Your Pre-Order Cart</h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleClearCart}
            className="btn btn-outline-danger btn-sm"
            disabled={cart.length === 0}
          >
            <FaTrash className="me-1" />
            Clear Cart
          </button>
          <button
            onClick={() => navigate('/products')}
            className="btn btn-outline-primary btn-sm"
          >
            <FaArrowLeft className="me-1" />
            Continue Shopping
          </button>
        </div>
      </div>
      
      <div className="row">
        <div className="col-12 col-lg-8 col-md-7">
          <div className="card">
            <div className="card-body">
              <h3 className="card-title mb-4">Pre-Order Items ({cart.length})</h3>
              
              {cart.map((item) => (
                <div key={item._id} className="cart-item">
                  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="cart-item-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80x80?text=Food';
                      }}
                    />
                    
                    <div className="cart-item-details flex-1 w-100">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="cart-item-title mb-1">{item.name}</h5>
                        <button
                          className="btn btn-danger btn-sm d-md-none"
                          onClick={() => handleRemoveItem(item)}
                          aria-label={`Remove ${item.name} from pre-order`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                      <p className="cart-item-price mb-2">₹{item.price}</p>
                      <p className="text-secondary text-sm mb-2">{item.description}</p>
                      <div className="mb-3 preorder-delivery-row">
                        <span className="badge bg-success preorder-badge">Pre-Order</span>
                        <small className="text-secondary delivery-info">
                          <FaCalendar className="me-1" />
                          Delivery: {deliveryDate.toLocaleDateString('en-GB')}
                        </small>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="quantity-controls d-flex align-items-center gap-3">
                          <button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            aria-label="Decrease quantity"
                          >
                            <FaMinus />
                          </button>
                          <span className="quantity-display">{item.quantity}</span>
                          <button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                            aria-label="Increase quantity"
                          >
                            <FaPlus />
                          </button>
                        </div>
                        
                        <div className="text-end d-none d-md-block">
                          <p className="h5 mb-2 text-primary">₹{(item.price * item.quantity).toFixed(2)}</p>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveItem(item)}
                            aria-label={`Remove ${item.name} from pre-order`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                        
                        <div className="text-end d-md-none">
                          <p className="h5 mb-0 text-primary">₹{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="col-12 col-lg-4 col-md-5 mt-4 mt-lg-0">
          <div className="card sticky-top" style={{ top: '2rem' }}>
            <div className="card-body">
              <h4 className="card-title mb-4">
                <FaShoppingCart className="me-2" />
                Pre-Order Summary
              </h4>
              
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal ({cart.length} items):</span>
                  <span className="fw-bold">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Delivery Fee:</span>
                  <span>{deliveryFee === 0 ? 'FREE' : `₹${deliveryFee.toFixed(2)}`}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <strong className="text-lg">Total:</strong>
                  <strong className="text-lg text-primary">₹{total.toFixed(2)}</strong>
                </div>
              </div>
              
              <div className="d-grid gap-3">
                <button
                  onClick={handleCheckout}
                  className="btn btn-primary btn-lg"
                  disabled={cart.length === 0 || loading || !isOrderingCurrentlyOpen()}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaShoppingCart className="me-2" />
                      Confirm Pre-Order
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => navigate('/products')}
                  className="btn btn-outline-primary"
                >
                  Continue Shopping
                </button>
              </div>
              
              <div className="mt-4 p-3 bg-light rounded">
                {settings?.enableDeliveryFee ? (
                  <>
                    {settings?.freeDeliveryEnabled && (
                      <p className="text-sm text-secondary mb-2">
                        <strong>Free delivery</strong> on orders over ₹{settings.freeDeliveryThreshold}! 🚚
                      </p>
                    )}
                    <p className="text-sm text-secondary mb-0">
                      <strong>Standard delivery fee:</strong> ₹{settings?.deliveryFee}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-secondary mb-0">
                    <strong>Free delivery</strong> on all orders! 🚚
                  </p>
                )}
                <p className="text-sm text-secondary mb-0 mt-2">
                  <strong>Batch delivery</strong> to your society on {deliveryDate.toLocaleDateString('en-GB')}
                </p>
              </div>
              
              {!isOrderingCurrentlyOpen() && (
                <div className="mt-3 p-3 bg-danger bg-opacity-20 rounded border border-danger">
                  <p className="text-sm text-danger mb-0">
                    <strong>⚠️ Ordering is currently closed!</strong><br />
                    Please wait for the next ordering cycle to open.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 